import { SelectChangeEvent, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemCreationToolTip } from '~/Components';
import { ProductCardsColorful } from '~/Components/Icons';
import { DARK_GREY, FADED_GREY, WHITE } from '~/Constants/Colors.constants';
import {
  FONT_SIZE_LARGE,
  FONT_SIZE_SMALL,
  FONT_SIZE_MEDIUM_INBETWEEN,
} from '~/Constants/UI.constants';
import { makeStyles } from '~/UIKit/Style';
import { QRCodePdfFilePreview, useQRCodePdfFilePreview } from './QRCodePdfFilePreview';
import { Button, MenuItem, Select } from '~/UIKit';
import { ExtendedLocation } from '../../ExtendedLocation.model';
import { useProvider } from '~/Hooks';
import { LANGUAGES } from '~/Constants/Languages.constants';
import { isEmpty } from 'lodash-es';
export const LocationEditCustomQRCode = ({
  extendedLocation,
  isCreateRoute,
}: {
  extendedLocation: ExtendedLocation;
  isCreateRoute?: boolean;
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { downloadQRCodeAsPdf } = useQRCodePdfFilePreview(extendedLocation.name);
  const { currentProvider } = useProvider();

  const containerRef = useRef(null);
  const isMapped = !isCreateRoute && !isEmpty(extendedLocation.checkInUrl);

  const [selectedLanguage, setSelectedLanguage] = useState(
    currentProvider?.language.primaryLanguage?.toUpperCase() || 'DE',
  );

  const onChangeLanguage = (e: SelectChangeEvent<any>) => setSelectedLanguage(e.target.value);

  const filteredLanguages = LANGUAGES.filter((item) => item !== 'it');

  return (
    <>
      <div className={classes.wrapper} ref={containerRef}>
        <div className={classes.descriptionWrapper}>
          <Typography className={classes.descriptionTitle}>
            {t('settings.title.qrCode')}{' '}
            <ItemCreationToolTip
              title={t('settings.title.qrCode.hint')}
              icon={<ProductCardsColorful />}
              containerRef={containerRef}
              placement="bottom-end"
            />
          </Typography>
          {!isMapped && (
            <Typography className={classes.description}>
              {t('settings.title.qrCode.info')}
            </Typography>
          )}
          <div className={classes.buttonWrapper}>
            <Button
              className={classes.button}
              variant="contained"
              color="primary"
              size="medium"
              onClick={downloadQRCodeAsPdf}
              disabled={!isMapped}
            >
              {t('app.download')}
            </Button>
          </div>
        </div>
        {isMapped && (
          <div className={classes.qrCodeWrapper}>
            <div className={classes.qrCodePreview}>
              <Typography className={classes.preview}>{t('app.preview').toUpperCase()}</Typography>
              <Select value={selectedLanguage} newDesign onChange={onChangeLanguage}>
                {filteredLanguages.map((item, index) => (
                  <MenuItem key={index} value={item.toUpperCase()}>
                    {item.toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <QRCodePdfFilePreview language={selectedLanguage} extendedLocation={extendedLocation} />
          </div>
        )}
      </div>
    </>
  );
};

const useStyles = makeStyles(
  (theme) => ({
    wrapper: {
      display: 'flex',

      [theme.breakpoints.down('md')]: {
        padding: theme.spacing(2),
        background: WHITE,
        flexDirection: 'column',
        alignItems: 'start',
        position: 'relative',
      },
    },
    descriptionWrapper: {
      width: '70%',
      marginBottom: theme.spacing(2),

      [theme.breakpoints.down('md')]: {
        width: '100%',
        marginBottom: theme.spacing(1),
      },
    },
    qrCodeWrapper: {
      [theme.breakpoints.down('md')]: {
        margin: '30px auto',
      },
      padding: `0 ${theme.spacing(1)}`,
      border: `solid 1px ${FADED_GREY}`,
      marginTop: theme.spacing(1),
    },
    preview: {
      padding: `${theme.spacing(1)} 0`,
      fontSize: FONT_SIZE_SMALL,
      color: DARK_GREY,
      fontWeight: 700,
    },
    descriptionTitle: {
      fontWeight: 'bolder',
      fontSize: FONT_SIZE_LARGE,
      display: 'flex',
      alignItems: 'center',
      width: 'fit-content',
    },
    description: {
      maxWidth: 270,
      marginTop: theme.spacing(1),
      fontSize: FONT_SIZE_MEDIUM_INBETWEEN,
    },
    buttonWrapper: {
      marginTop: theme.spacing(3),
      [theme.breakpoints.down('md')]: {
        textAlign: 'center',
      },
    },
    button: {
      width: 218,
    },
    qrCodePreview: {
      padding: 4,
      display: 'flex',
      justifyContent: 'space-between',
    },
  }),
  {
    name: 'LocationEditCustomQRCode',
  },
);
